import {useCallback, useRef, useState} from 'react';
import {FormikHelpers} from 'formik';
import {useAppDispatch} from 'hooks';
import {clearSubmitStatus} from 'admin/resources/upload/store';
import {TResourceSubmitStatus} from 'admin/resources/upload/store/IUploadStoreState';
import {snapshotResourceFormValues} from './runResourceFormSubmit';
import {useResourceSubmitOutcome} from './useResourceSubmitOutcome';

type TUploadArea = 'file' | 'scormPackage' | 'webLink';

type TFormHelpers<T> = Pick<FormikHelpers<T>, 'resetForm' | 'setValues'>;

/**
 * On error: restore Formik in place (no remount — keeps File / field UI).
 * On success: remount so debounced inputs (Keywords, etc.) discard local state.
 */
export const useResourceUploadFormLifecycle = <T>(
  blankValues: T,
  submitStatus: TResourceSubmitStatus,
  area: TUploadArea
) => {
  const dispatch = useAppDispatch();
  const blankRef = useRef(blankValues);
  blankRef.current = blankValues;

  const [formKey, setFormKey] = useState(0);
  const latestValuesRef = useRef<T>(blankValues);
  const frozenValuesRef = useRef<T | null>(null);
  const helpersRef = useRef<TFormHelpers<T> | null>(null);

  const capturePreSubmit = useCallback((values: T) => {
    frozenValuesRef.current = snapshotResourceFormValues(values);
  }, []);

  const registerFormik = useCallback((values: T, helpers: TFormHelpers<T>) => {
    latestValuesRef.current = values;
    helpersRef.current = helpers;
  }, []);

  const clear = useCallback(() => {
    dispatch(clearSubmitStatus(area));
  }, [area, dispatch]);

  const restorePreSubmit = useCallback(() => {
    const snapshot = frozenValuesRef.current ?? latestValuesRef.current;
    helpersRef.current?.setValues(snapshotResourceFormValues(snapshot));
  }, []);

  const clearToBlank = useCallback(() => {
    helpersRef.current?.resetForm({values: blankRef.current});
    setFormKey((key) => key + 1);
  }, []);

  useResourceSubmitOutcome(submitStatus, clearToBlank, restorePreSubmit, clear);

  return {formKey, capturePreSubmit, registerFormik};
};
